import { CommonModule, DatePipe, NgFor } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import { PermissionsEnum} from '../../../api/api-reference';
import {
  Client,
  GuidDictionaryItem,
  MeterAutentification,
  MeterInterface,
  MeterSecurity,
} from '../../../api/api-reference';
import { AuthService } from '../../../services/auth.service';
import { FutureDatepickerComponent } from '../../core/components/future-datepicker/future-datepicker.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { TariffDataService } from '../services/tariff-data.service';
import { toUtcDate } from '../../../helper/date-utils';

@Component({
  selector: 'app-tariff-setup',
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    NgFor,
    HttpClientModule,
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    FutureDatepickerComponent,
  ],
  providers: [Client, DatePipe],
  templateUrl: './tariff-setup.component.html',
  styleUrl: './tariff-setup.component.scss',
})
export class TariffSetupComponent implements OnInit {
  showPassword: boolean = false;
  hideAdd: boolean = false;
  startDate: Date = new Date();

  region: string | null = '';
  data: CreateTariffDataIn = new CreateTariffDataIn();
  regions: GuidDictionaryItem[] = [];
  serviceTypes: GuidDictionaryItem[] = [];
  meterModels: GuidDictionaryItem[] = [];
  meterMakers: GuidDictionaryItem[] = [];
  areas: GuidDictionaryItem[] = [];
  assetTypes: GuidDictionaryItem[] = [];
  interface: MeterInterface | undefined;
  authorizationLevel: MeterAutentification | undefined;
  clientAddressField: number | undefined;
  meterSecurity: MeterSecurity | undefined;

  MeterAutentification = MeterAutentification;

  // Separate state for each datepicker
  isStartDatepickerShown = false;
  isEndDatepickerShown = false;

  selectedStartDate: string | null = '';
  selectedEndDate: string | null = '';

  // Date validation error
  dateRangeError: string | null = null;

  public PermissionsEnum = PermissionsEnum;

  // Tariff labels for the preview components
  tariffLabels: string[] = [
    'Government Levy',
    'Street Lighting',
    'VAT',
    'NHL & GetFund',
    'Subsidy'
  ];

  tariffPreviewList: TariffPreviewDataOut[] | undefined = [];

  addAnotherDevice(): void {
    this.hideAdd = false;
    this.data.reset();
  }

  editingDeviceId: string | null = null;
  @ViewChild('crudform') crudform!: NgForm;

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    private datePipe: DatePipe,
    public authService: AuthService,
    public tariffDataService: TariffDataService,
  ) {
    this.tariffPreviewList = [
      { id: "1", values: ["BandA", "SLT", "Prepaid", "Single"] },
      { id: "2", values: ["BandB", "SLT", "Postpaid", "Multiple"] },
      { id: "3", values: ["BandC", "Two", "Three"] },
      { id: "4", values: ["A", "B", "C"] },
    ];
  }

  ngOnInit(): void {
    const stateData = history.state?.data;
    this.editingDeviceId = history.state?.editingId;

    if (stateData) {
      // First assign the data
      Object.assign(this.data, stateData);
      this.data.id = undefined;

      // Initialize date fields if they exist
      if (this.data.tariffActivePeriodStartDate) {
        this.selectedStartDate = this.datePipe.transform(
          this.data.tariffActivePeriodStartDate,
          'dd MMM yyyy'
        );
      }
      if (this.data.tariffActivePeriodEndDate) {
        this.selectedEndDate = this.datePipe.transform(
          this.data.tariffActivePeriodEndDate,
          'dd MMM yyyy'
        );
      }
    }
    this.getServiceTypes();
  }

  tariffServiceType: TariffServiceType[] = Object.keys(TariffServiceType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => TariffServiceType[key as keyof typeof TariffServiceType]);

  tariffCustomerType: TariffCustomerType[] = Object.keys(TariffCustomerType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => TariffCustomerType[key as keyof typeof TariffCustomerType]);

  tariffClassCategory: TariffClassCategory[] = Object.keys(TariffClassCategory)
    .filter((key) => isNaN(Number(key)))
    .map((key) => TariffClassCategory[key as keyof typeof TariffClassCategory]);
    
  tariffBand: TariffBand[] = Object.keys(TariffBand)
    .filter((key) => isNaN(Number(key)))
    .map((key) => TariffBand[key as keyof typeof TariffBand]);

  onSubmit() {
    // Validate date range before submit
    if (!this.isDateRangeValid()) {
      this.toastr.error('Start date must be less than end date');
      return;
    }

    if (this.editingDeviceId) {
      // Update existing device

      const success = this.tariffDataService.updateTariffDevice(this.editingDeviceId, this.data);
      if (success) {
        this.toastr.success('Tariff updated successfully');
      } else {
        this.toastr.error('Failed to update tariff');
        return;
      }
    } else {
      // Add new device
      const deviceId = this.tariffDataService.addTariffDevice(this.data);
      this.hideAdd = true;
      console.log(this.tariffDataService.getTariffDevices().length);
      this.toastr.success('Tariff added successfully');
    }
  }

  getServiceTypes() {
    this.client.getAllServiceTypesForOptions().subscribe({
      next: (response) => {
        this.serviceTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  setInputDate(event: string | number | Date): Date {
    const inputDate = new Date(event);
    return inputDate;
  }

  toggleStartDatepicker() {
    this.isStartDatepickerShown = !this.isStartDatepickerShown;
    if (this.isStartDatepickerShown) {
      this.isEndDatepickerShown = false; // Close end date picker if open
    }
  }

  toggleEndDatepicker() {
    this.isEndDatepickerShown = !this.isEndDatepickerShown;
    if (this.isEndDatepickerShown) {
      this.isStartDatepickerShown = false; // Close start date picker if open
    }
  }

  onStartDateRangeChanged(input: DateRangeInput): void {
    const startDate = new Date(input.dateFrom);
    this.data.tariffActivePeriodStartDate = toUtcDate(startDate);
    this.selectedStartDate = this.datePipe.transform(startDate, 'dd MMM yyyy');
    this.validateDateRange();
    this.toggleStartDatepicker();
  }

  onEndDateRangeChanged(input: DateRangeInput): void {
    const endDate = new Date(input.dateFrom);
    this.data.tariffActivePeriodEndDate = toUtcDate(endDate);
    this.selectedEndDate = this.datePipe.transform(endDate, 'dd MMM yyyy');
    this.validateDateRange();
    this.toggleEndDatepicker();
  }

  /**
   * Validates that start date is less than end date
   */
  isDateRangeValid(): boolean {
    if (!this.data.tariffActivePeriodStartDate || !this.data.tariffActivePeriodEndDate) {
      return true; // No validation needed if either date is missing
    }

    const startDate = new Date(this.data.tariffActivePeriodStartDate);
    const endDate = new Date(this.data.tariffActivePeriodEndDate);

    return startDate <= endDate;
  }

  validateDateRange(): void {
    if (this.data.tariffActivePeriodStartDate && this.data.tariffActivePeriodEndDate) {
      const startDate = new Date(this.data.tariffActivePeriodStartDate);
      const endDate = new Date(this.data.tariffActivePeriodEndDate);

      if (startDate > endDate) {
        this.dateRangeError = 'Start date must be less than or equal to end date';
      } else {
        this.dateRangeError = null;
      }
    } else {
      this.dateRangeError = null; 
    }
  }

  preventInvalidInput(event: KeyboardEvent): void {
    const invalidChars = ['e', 'E', '+', '-', '.'];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }
}

export class TariffPreviewDataOut {
  id!: string;
  values!: string[];
}

export class CreateTariffDataIn {
  id?: string;
  serviceName?: string;                 // text input
  tariffServiceType?: string;           // radio
  tariffCustomerType?: string;          // radio
  tariffClassCategory?: string;         // radio, uslovno prikazano
  tariffBand?: string;                  // radio
  tariffActivePeriodStartDate?: Date; // text date
  tariffActivePeriodEndDate?: Date;   // text date
  tariffRate?: string;                  // text
  tariffServiceChangeRate?: string;     // text
  tariffMaxDemandRate?: string;         // text
  tariffGovernmentLevy?: string;       // text
  tariffNhlAndGetFund?: string;       // text
  tariffSubsidy?: string;              // text
  tariffStreetLighting?: string;      // text
  tariffVat?: string;                  // text

  reset() {
    this.serviceName = '';
    this.tariffServiceType = undefined;
    this.tariffCustomerType = undefined;
    this.tariffClassCategory = undefined;
    this.tariffBand = undefined;
    this.tariffActivePeriodStartDate = undefined;
    this.tariffActivePeriodEndDate = undefined;
    this.tariffRate = '';
    this.tariffServiceChangeRate = '';
    this.tariffMaxDemandRate = '';
    this.tariffGovernmentLevy = '';
    this.tariffNhlAndGetFund = '';
    this.tariffSubsidy = '';
    this.tariffStreetLighting = '';
    this.tariffVat = '';
  }
}

export enum TariffServiceType {
    Prepaid = "Prepaid",
    Postpaid = "Postpaid",
}

export enum TariffCustomerType {
    Nslt = "NSLT",
    SLT = "SLT",
}

export enum TariffClassCategory {
    Residential = "Residential prepaid",
    NonResidential = "Nonresidential prepaid",
}

export enum TariffBand {
    Single = "Single",
    Multiple = "Multiple",
}